import { CAMPAIGN_CONFIRM_SKIP_REASONS, CampaignConfirmSkipReason } from "@signalist/shared-types";

import { AssignStatus, InclusionStatus, SelectType } from "@/enum/list.enum";

/** The parts of a loaded lead row "Confirm preassigned campaigns" decides on. */
export interface ConfirmableRow {
	assignStatus: AssignStatus;
	inclusionStatus: InclusionStatus;
	campaignId?: string | null;
	prospect?: { name?: string | null } | null;
}

/** The list's campaigns as the row's campaign is checked against them (`list.campaignConfig`). */
export type ConfirmCampaignConfig = Record<string, unknown> | null | undefined;

/** The reasons the browser can tell from a loaded row; the rest only the server knows. */
export type ClientConfirmSkipReason = Exclude<CampaignConfirmSkipReason, "FAILED">;

/** The API's placeholder prospects (`PLACEHOLDER_PROSPECT_NAMES`, apps/api/src/list/constants.ts:3). */
const PLACEHOLDER_NAMES: readonly string[] = ["Person not found", "Company not found", "SELECT"];

/** The pre-enrollment statuses (`ROUTABLE_ASSIGN_STATUSES`, campaign-routable.util.ts:22). */
const PRE_ENROLLMENT: readonly AssignStatus[] = [AssignStatus.DRAFT, AssignStatus.SAVED_FOR_LATER];

const isMarkedValid = (config: ConfirmCampaignConfig, campaignId: string): boolean => {
	const entry = config?.[campaignId];

	return (
		typeof entry === "object" && entry !== null && (entry as { isValid?: unknown }).isValid === true
	);
};

/**
 * Why the server's "Confirm preassigned" would leave this lead where it is, or null when it would
 * enroll it into its own campaign. Mirrors `confirmSkipReasonOf`
 * (apps/api/src/list/campaign-routing/campaign-bulk-routing.util.ts:44-71) in the same order: a
 * placeholder, a lead outside the list's leads, one past the pre-enrollment statuses, one without a
 * campaign, one whose campaign is gone from the list or not marked valid. What a row does not carry
 * (a live schedule, a campaign being deleted, an empty message step) only the server sees; its
 * answer reports those.
 */
export const confirmSkipReasonOfRow = (
	row: ConfirmableRow,
	campaignConfig: ConfirmCampaignConfig
): ClientConfirmSkipReason | null => {
	if (PLACEHOLDER_NAMES.includes(row.prospect?.name ?? "")) {
		return "PLACEHOLDER";
	}

	if (row.inclusionStatus !== InclusionStatus.ADDED) {
		return "NOT_INCLUDED";
	}

	if (!PRE_ENROLLMENT.includes(row.assignStatus)) {
		return "ALREADY_ENROLLED";
	}

	if (!row.campaignId) {
		return "NO_CAMPAIGN";
	}

	return isMarkedValid(campaignConfig, row.campaignId) ? null : "CAMPAIGN_NOT_USABLE";
};

/**
 * What the dialog can say before the commit. `counted`: every selected lead is loaded, so it names
 * how many it confirms and how many it skips, per reason. `serverResolved`: a select-all (or a
 * selection not all loaded), whose leads the server derives, so no exact number is promised.
 */
export type ConfirmPlan =
	| {
			kind: "counted";
			eligible: number;
			skipped: { reason: ClientConfirmSkipReason; count: number }[];
	  }
	| { kind: "serverResolved"; anyEligibleLoaded: boolean };

export interface ConfirmSelection {
	selectType: SelectType;
	selectedItems: readonly number[];
	selectedRows: readonly ConfirmableRow[];
	campaignConfig: ConfirmCampaignConfig;
}

/** The skips counted per reason, in the contract's order, without empty reasons. */
const countSkips = (reasons: readonly ClientConfirmSkipReason[]) =>
	CAMPAIGN_CONFIRM_SKIP_REASONS.filter(
		(reason): reason is ClientConfirmSkipReason => reason !== "FAILED"
	)
		.map((reason) => ({ reason, count: reasons.filter((r) => r === reason).length }))
		.filter((entry) => entry.count > 0);

/** The confirm plan of a bulk selection, from its loaded rows. */
export const confirmPlanOf = (selection: ConfirmSelection): ConfirmPlan => {
	const reasons = selection.selectedRows.map((row) =>
		confirmSkipReasonOfRow(row, selection.campaignConfig)
	);
	const eligible = reasons.filter((reason) => reason === null).length;
	const allLoaded = selection.selectedRows.length >= selection.selectedItems.length;

	if (selection.selectType === SelectType.ALL || !allLoaded) {
		return { kind: "serverResolved", anyEligibleLoaded: eligible > 0 };
	}

	return {
		kind: "counted",
		eligible,
		skipped: countSkips(reasons.filter((reason): reason is ClientConfirmSkipReason => !!reason)),
	};
};

/**
 * Whether to offer "Confirm preassigned campaigns": the selection holds at least one lead the server
 * would confirm. For a select-all the loaded page stands for the selection, as it does for the
 * blacklist gate (PREC-2548): a tab whose loaded leads are all enrolled does not offer it.
 */
export const canConfirmPlan = (plan: ConfirmPlan): boolean =>
	plan.kind === "counted" ? plan.eligible > 0 : plan.anyEligibleLoaded;
